import { useCallback, useEffect, useState } from 'react';

/** Admin session stored in sessionStorage (dies with the tab).
 *  Shape: { username, password, permissions, since }
 *  TTL: 24 h. */
const KEY = 'fluo_admin_session';
const TTL_MS = 24 * 60 * 60 * 1000;

const read = () => {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj?.username || !obj?.password || !obj.since) return null;
    if (Date.now() - obj.since > TTL_MS) return null;
    return obj;
  } catch { return null; }
};

const listeners = new Set();
const notify = () => listeners.forEach(fn => fn());

export const useAdminAuth = () => {
  const [session, setSession] = useState(() => read());

  useEffect(() => {
    const fn = () => setSession(read());
    listeners.add(fn);
    return () => listeners.delete(fn);
  }, []);

  const login = useCallback(async (username, password) => {
    const API = process.env.REACT_APP_BACKEND_URL;
    const res = await fetch(`${API}/api/admin/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      const e = new Error(err.detail || 'Identifiants invalides');
      e.status = res.status;
      throw e;
    }
    const data = await res.json();
    const obj = { username, password, permissions: data.permissions || {}, since: Date.now() };
    sessionStorage.setItem(KEY, JSON.stringify(obj));
    setSession(obj);
    notify();
  }, []);

  const logout = useCallback(() => {
    sessionStorage.removeItem(KEY);
    setSession(null);
    notify();
  }, []);

  return {
    isAuthenticated: !!session,
    username:    session?.username    || null,
    password:    session?.password    || null,
    permissions: session?.permissions || {},
    login,
    logout,
  };
};
